'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';

const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_ID;

/**
 * GA через Consent Mode v2: gtag грузится ВСЕГДА (отложенно, чтобы не мешать LCP),
 * но до явного «Принять» работает с analytics_storage='denied' — без cookie,
 * анонимными пингами.
 *
 * Почему не «грузить только после согласия» (как было с 2026-07-10 по 2026-08-11):
 * жёсткий гейт делал невидимыми ВСЕХ, кто нажал «Только необходимые» или
 * проигнорировал баннер, — вместе с их affiliate_click. Аудит 2026-08-10 (§2):
 * недельное отношение GA4-сессий к GSC-кликам упало с 0.99–1.08 до 0.70–0.87
 * ровно в день деплоя гейта (~650 потерянных сессий/мес), а конверсии терялись
 * ещё сильнее — trackClick.js молча дропает событие, если window.gtag нет.
 *
 * Согласие: дефолт denied выставляется до config; CookieBanner шлёт
 * gtag('consent','update',...) на принятие/отказ, здесь же дефолт повышается
 * сразу, если согласие уже сохранено в localStorage с прошлого визита.
 */
export default function GoogleAnalytics() {
    const [shouldLoad, setShouldLoad] = useState(false);

    useEffect(() => {
        if (!GA_TRACKING_ID) return;

        let idleId;
        let timerId;
        if ('requestIdleCallback' in window) {
            idleId = window.requestIdleCallback(() => setShouldLoad(true), { timeout: 4000 });
        } else {
            timerId = window.setTimeout(() => setShouldLoad(true), 4000);
        }
        return () => {
            if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
            if (timerId) window.clearTimeout(timerId);
        };
    }, []);

    if (!GA_TRACKING_ID || !shouldLoad) return null;

    return (
        <>
            <Script
                src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
                strategy="lazyOnload"
            />
            <Script id="google-analytics" strategy="lazyOnload">
                {`
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    window.gtag = window.gtag || gtag;

                    // Consent Mode v2: дефолт — всё запрещено, до config.
                    gtag('consent', 'default', {
                        'analytics_storage': 'denied',
                        'ad_storage': 'denied',
                        'ad_user_data': 'denied',
                        'ad_personalization': 'denied'
                    });

                    // Согласие, сохранённое в прошлый визит, применяем сразу —
                    // CookieBanner в этом случае не показывается и update не пошлёт.
                    try {
                        if (localStorage.getItem('cookieConsent') === 'accepted') {
                            gtag('consent', 'update', { 'analytics_storage': 'granted' });
                        }
                    } catch (e) {}

                    gtag('js', new Date());
                    gtag('config', '${GA_TRACKING_ID}', {
                        page_title: document.title,
                        page_location: window.location.href,
                    });
                `}
            </Script>
        </>
    );
}

// Функция для отслеживания событий
export const trackEvent = (action, category, label, value) => {
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', action, {
            event_category: category,
            event_label: label,
            value: value,
        });
    }
};
